import React, { useState, useRef } from "react";
import SignaturePad from "react-signature-canvas";

import EditIcon from '@mui/icons-material/Edit';
import MandatoryAlertIcon from './MandatoryAlertIcon';

import LoaderNew from "../../LoaderNew";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  ButtonBase,
} from '@mui/material';

import { withStyles } from '@mui/styles';
import CommonStyles from "../../CommonStyles";

const styles = {
  sigPad: {
    position: 'absolute',
    margin: '0 auto',
    padding: '0 0 0 0',
    width: '550px',
    height: '300px',
    backgroundColor: 'white',
  },
  dialog: {
    margin: '0 auto',
    position: 'relative',
    overflow: 'hidden',
  },
  dialogContent: {
    position: 'relative',
    width: '550px',
    height: '300px',
    padding: '0 0 0 0',
  },
  dialogTitle: {
    textAlign: 'center',
  },
  cancelBtn: {
    fontSize: 14,
    color: '#878787',
    borderRadius: '10px',
    textTransform: 'none',
    background: 'transparent',
    padding: '6px 20px',
    '&:hover': {
      color: '#E91818',
    },
  },
  clearlBtn: {
    fontSize: 14,
    color: '#878787',
    borderRadius: '10px',
    textTransform: 'none',
    background: 'transparent',
    padding: '6px 20px',
    '&:hover': {
      color: '#347CFF',
    },
  },
  acceptBtn: {
    fontSize: 14,
    color: '#222029',
    borderRadius: '10px',
    textTransform: 'none',
    padding: '6px 20px',
    opacity: '0.9',
    '&:hover': {
      color: '#FFFF',
      opacity: '1.0',
      background: '#222029',
    },
  },
  signatureAddButton: {
    width: '100px',
    height: '24px',
    padding: '3px 16px',
    fontSize: '11.5px',
    cursor: 'pointer',
    boxShadow: 'none',
    borderRadius: '10px',
    lineHeight: 'normal',
    textTransform: 'none',
    backgroundColor: 'rgb(225, 234, 255)',
    border: '1px solid #ffffff',
    outline: '1px solid #e1eaff',
    color: 'rgb(62, 81, 123)',
    '&:hover': {
      color: 'rgb(18, 18, 18)',
      outline: '1px solid rgb(186, 205, 235)',
      '& .sign-icon path': {
        fill: 'rgb(18, 18, 18)'
      },
    },
  },
  signImageContainer: {
    cursor: 'pointer',
    display: 'contents',
  },
  signImage: {
    cursor: 'pointer',
    maxWidth: '120px',
    maxHeight: '50px',
  },
  ...CommonStyles,
};

export default withStyles(styles)(({
  rst,
  trpl,
  setTrpl,
  classes,
  isNewNode,
  ancestorId,
  loadingTids,
  setLoadingTids,
  showMandatoryAlert,
  disableTrplEditing,
}) => {
  const [opened, setOpened] = useState(false);

  const sigCanvas = useRef({});

  /* a function that uses the canvas ref to clear the canvas 
  via a method given by react-signature-canvas */
  const clear = () => sigCanvas.current.clear();

  /* a function that uses the canvas ref to trim the canvas 
  from white spaces via a method given by react-signature-canvas
  then saves it in our state */
  const save = () => {
    const imageUrl = sigCanvas.current.getTrimmedCanvas().toDataURL();

    if (imageUrl) setTrpl(ancestorId, rst, imageUrl, false, trpl && trpl.tid ? trpl.tid : undefined, setLoadingTids);

    setOpened(false);
  };

  const imageURL = trpl && trpl.metadata && trpl.metadata._val ? trpl.metadata._val : null;
  const rstId = Array.isArray(rst.id) ? rst.id[rst.id.length - 1] : rst.id;

  return (
    <React.Fragment>
      <Dialog
        open={opened}
        aria-labelledby="sign-trpl"
        onClose={() => setOpened(false)}
        onEscapeKeyDown={() => setOpened(false)}
        className={classes.dialog}
      >
        <div style={{ height: '3px', background: '#222029' }}></div>
        <DialogContent className={classes.dialogContent}>
          <SignaturePad
            ref={sigCanvas}
            canvasProps={{ className: classes.sigPad }}
          />
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-around', backgroundColor: '#FBFBFB' }}>
          <Button
            className={classes.cancelBtn}
            onClick={() => setOpened(false)}
          >
            Cancel
          </Button>
          <Button
            className={classes.clearlBtn}
            onClick={() => clear()}
          >
            Clear
          </Button>
          <Button
            className={classes.acceptBtn}
            onClick={() => save()}
          >
            Confirm
        </Button>
        </DialogActions>
      </Dialog>

      <div key={`${rst.title} ${rst.id}`}
        style={{
          display: 'flex',
          minHeight: '24px',
          minWidth: '50px',
          alignItems: 'center',
        }}
      >
        {imageURL ? (
          <div
            className={classes.signImageContainer}            
            onClick={() => setOpened(true)}
          >
            <img
              className={classes.signImage}
              src={imageURL}
              alt="my-signature"
            />
            <EditIcon
              className={'edit-sign'}
              style={{
                fontSize: 18,
                alignSelf: 'center',
                marginLeft: '5px',
                color: '#c4c4c4',
              }}
            />
          </div>
        ) :
        <ButtonBase
          size="small"
          disabled={isNewNode || disableTrplEditing}
          className={`${classes.signatureAddButton} sign-button`}
          classes={{
            disabled: classes.btnNormalDisabled,
            focusVisible: classes.focusable,
          }}
          onClick={() => setOpened(true)}
        >
          {loadingTids.includes(rstId) ? <LoaderNew style={{ zoom: 0.8 }}/> :
            <React.Fragment>
              <span className={classes.ml10}>Sign</span>
              <div style={{ margin: '5px 13px 0 7px' }}>
                <svg className={`sign-icon`} width="15" height="14" viewBox="0 0 17 16" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_5958_14372)">
                    <path d="M0.775824 15.4762C0.560193 15.6487 0.525231 15.9634 0.697735 16.179C0.870239 16.3947 1.18489 16.4296 1.40052 16.2571L0.775824 15.4762ZM6.96262 0.499985L6.8919 0.994959L6.96262 0.499985ZM2.42928 12.0028L2.07573 12.3563L2.42928 12.0028ZM6.96262 13.1361L7.14832 13.6003L6.96262 13.1361ZM10.9998 4.99998L10.9998 4.49998L10.9998 4.99998ZM17.4191 8.6857C17.5216 8.42931 17.3969 8.13832 17.1405 8.03577C16.8841 7.93321 16.5931 8.05792 16.4906 8.31431L17.4191 8.6857ZM1.40052 16.2571C2.17458 15.6379 3.17987 14.3719 4.17628 12.8648C5.18272 11.3425 6.21293 9.52363 7.02935 7.75543C7.84089 5.99782 8.46105 4.24826 8.61355 2.8766C8.68936 2.19478 8.65734 1.54414 8.42045 1.02307C8.16639 0.464243 7.69573 0.09964 7.03333 0.00501012L6.8919 0.994959C7.22117 1.042 7.39922 1.19302 7.51011 1.43693C7.63816 1.7186 7.68731 2.15781 7.61968 2.7661C7.48531 3.97464 6.9202 5.60634 6.12146 7.33623C5.32761 9.05554 4.32254 10.8304 3.3421 12.3133C2.35163 13.8114 1.41844 14.9622 0.775824 15.4762L1.40052 16.2571ZM7.03333 0.00501012C5.72176 -0.182359 4.59647 0.428587 3.72028 1.37477C2.84783 2.31691 2.17581 3.63412 1.72748 5.01196C1.27797 6.39337 1.04059 7.87466 1.06533 9.17075C1.0895 10.4373 1.36686 11.6475 2.07573 12.3563L2.78284 11.6492C2.35837 11.2248 2.08806 10.3522 2.06515 9.15166C2.0428 7.98067 2.25891 6.61057 2.6784 5.32138C3.09906 4.02861 3.71154 2.856 4.45401 2.05421C5.19274 1.25646 6.01279 0.869371 6.8919 0.994959L7.03333 0.00501012ZM2.07573 12.3563C2.18063 12.4612 2.28425 12.5731 2.40071 12.6975C2.51448 12.8191 2.63816 12.95 2.77109 13.0782C3.03783 13.3353 3.35594 13.5954 3.75799 13.784C4.58917 14.1737 5.6494 14.1999 7.14832 13.6003L6.77693 12.6719C5.44251 13.2056 4.68516 13.1143 4.18255 12.8786C3.9177 12.7544 3.68987 12.5749 3.4651 12.3582C3.35228 12.2494 3.24383 12.1349 3.13077 12.0142C3.02039 11.8962 2.90248 11.7689 2.78284 11.6492L2.07573 12.3563ZM7.14832 13.6003C7.8086 13.3362 8.61653 12.6447 9.37401 11.8352C10.1485 11.0074 10.9305 9.99058 11.5331 8.99738C12.1239 8.02377 12.5869 7.00128 12.6392 6.17926C12.666 5.75879 12.5893 5.31513 12.281 4.97675C11.9698 4.63516 11.5134 4.49999 10.9998 4.49998L10.9998 5.49998C11.3525 5.49999 11.4884 5.59157 11.5418 5.65024C11.5982 5.7121 11.6586 5.8422 11.6412 6.11574C11.6052 6.68172 11.2549 7.52824 10.6782 8.47865C10.1134 9.40947 9.37396 10.3716 8.64382 11.1519C7.89665 11.9504 7.21676 12.4959 6.77693 12.6719L7.14832 13.6003ZM10.9998 4.49998C9.79712 4.49998 8.96705 5.1344 8.46829 6.02744C7.98408 6.89441 7.80351 8.0085 7.81779 9.07473C7.83212 10.1456 8.0437 11.2291 8.40095 12.0619C8.57915 12.4774 8.80412 12.8552 9.08048 13.1367C9.35809 13.4196 9.72063 13.6361 10.1548 13.6361L10.1548 12.6361C10.0743 12.6361 9.95325 12.5984 9.79416 12.4363C9.6338 12.2729 9.46895 12.015 9.31996 11.6677C9.02284 10.975 8.83059 10.0245 8.8177 9.06135C8.80474 8.09354 8.97354 7.1736 9.34135 6.51505C9.69459 5.88257 10.2192 5.49998 10.9998 5.49998L10.9998 4.49998ZM10.1548 13.6361C10.8751 13.6361 11.4344 13.2271 11.8569 12.7534C12.2795 12.2798 12.6286 11.671 12.9243 11.128C13.2355 10.5567 13.4777 10.0797 13.7142 9.76197C13.8321 9.60355 13.911 9.53872 13.9486 9.51829C13.9614 9.51137 13.9548 9.51768 13.9334 9.51856C13.9101 9.51953 13.8959 9.51291 13.8979 9.51389L14.3451 8.61946C14.0413 8.46755 13.7322 8.49795 13.4716 8.63938C13.2419 8.76403 13.0582 8.96842 12.912 9.16495C12.6202 9.55707 12.3276 10.1328 12.0461 10.6497C11.7492 11.195 11.4479 11.7098 11.1107 12.0877C10.7736 12.4656 10.464 12.6361 10.1548 12.6361L10.1548 13.6361ZM13.8979 9.51389C13.9029 9.51639 13.8959 9.51346 13.8883 9.50316C13.8816 9.49418 13.8842 9.49277 13.8881 9.51015C13.8982 9.55515 13.901 9.65372 13.8631 9.82817C13.787 10.1783 13.5978 10.6248 13.3786 11.1364C13.1728 11.6165 12.937 12.1617 12.836 12.6261C12.7854 12.8589 12.756 13.1234 12.8123 13.375C12.8744 13.6521 13.0396 13.9014 13.3312 14.0472L13.7784 13.1528C13.7734 13.1503 13.7804 13.1532 13.7881 13.1635C13.7947 13.1725 13.7921 13.1739 13.7882 13.1565C13.7781 13.1115 13.7753 13.013 13.8132 12.8385C13.8893 12.4884 14.0785 12.0418 14.2977 11.5303C14.5035 11.0502 14.7393 10.505 14.8403 10.0406C14.8909 9.80775 14.9203 9.5433 14.864 9.29168C14.8019 9.01455 14.6367 8.76527 14.3451 8.61946L13.8979 9.51389ZM13.3312 14.0472C13.684 14.2236 14.0321 14.1209 14.2807 13.974C14.5248 13.8298 14.75 13.6031 14.9502 13.36C15.3555 12.8678 15.7642 12.1757 16.1223 11.4954C16.4838 10.8085 16.8077 10.1065 17.0405 9.5782C17.1571 9.31353 17.2514 9.09132 17.3167 8.93493C17.3493 8.85671 17.3747 8.7949 17.392 8.7524C17.4007 8.73115 17.4074 8.71472 17.4119 8.70349C17.4142 8.69787 17.4159 8.69355 17.4171 8.69057C17.4177 8.68908 17.4182 8.68792 17.4185 8.68711C17.4187 8.6867 17.4188 8.68638 17.4189 8.68615C17.4189 8.68603 17.419 8.68592 17.419 8.68586C17.419 8.68577 17.4191 8.6857 16.9548 8.50001C16.4906 8.31431 16.4906 8.31428 16.4906 8.31428C16.4906 8.31431 16.4906 8.31433 16.4906 8.31438C16.4905 8.31449 16.4904 8.31468 16.4903 8.31496C16.4901 8.31552 16.4897 8.31642 16.4892 8.31765C16.4883 8.32012 16.4867 8.32391 16.4847 8.329C16.4806 8.33917 16.4744 8.3545 16.4661 8.37461C16.4497 8.41485 16.4253 8.47422 16.3938 8.54985C16.3306 8.70113 16.239 8.91721 16.1254 9.17494C15.8978 9.69142 15.5842 10.3706 15.2374 11.0296C14.8871 11.6951 14.5166 12.3134 14.1782 12.7243C14.0065 12.9328 13.8687 13.0559 13.7719 13.1131C13.6796 13.1676 13.709 13.1181 13.7784 13.1528L13.3312 14.0472Z" fill="#4E4C4C"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_5958_14372">
                      <rect width="17" height="17" fill="white" transform="translate(0.337402)"/>
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </React.Fragment>
          }
        </ButtonBase>}
        {(showMandatoryAlert && imageURL === null) && <MandatoryAlertIcon />}
      </div>
    </React.Fragment>
  );
})
